import { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { saveAs } from 'file-saver';
import {
  InformationCircleIcon,
  DocumentArrowDownIcon,
  TableCellsIcon,
} from '@heroicons/react/20/solid';
import { supabaseClient } from '../../config/supabase-clients';
import {
  SERIE as SERIE_ESTATICA,
  ejecutarSimulacion,
  simular,
  getIndice,
  totales,
  calcularTAEAproximada,
  agregarAnual,
  eur,
  fmt,
  pct,
  ym,
} from '../../utils';

// Estado inicial = equivalente a cargarEjemplo() del HTML original.
const FORM_INICIAL = {
  cliente: 'Cliente Ejemplo — IRPH/2026/001',
  capital: '150000',
  plazo: '360',
  fechaFirma: '2007-06',
  periodRev: '12',
  offsetRev: '-1',
  carencia: '0',
  indiceIRPH: 'IRPH_CAJAS',
  diffIRPH: '0.25',
  tipoFijoMeses: '12',
  tipoFijoValor: '4.85',
  suelo: '',
  techo: '',
  redondeo: '0.25',
  redondeoAlza: true,
  trasSupresion: 'DA15',
  indiceAlt: 'EUR_MIBOR',
  diffAlt: '0',
  altBEnabled: true,
  indiceAltB: 'EUR_MIBOR',
  diffAltB: '1',
  fechaCorte: '2026-04',
  anticipos: [],
};

// Límites de los selectores de mes: la serie oficial del BdE arranca en
// enero de 1990 y no tiene sentido seleccionar un mes futuro.
const MES_MIN = '1990-01';
const _HOY = new Date();
const MES_MAX = `${_HOY.getFullYear()}-${String(_HOY.getMonth() + 1).padStart(2, '0')}`;

const inputCls =
  'w-full min-w-0 px-2.5 py-2 border border-gray-300 rounded-md text-sm bg-white text-gray-900 ' +
  'focus:outline-none focus:ring-2 focus:ring-primary-500/40 focus:border-primary-600 ' +
  'dark:bg-gray-700 dark:border-gray-600 dark:text-white';
const labelCls = 'text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block';

const btnBase =
  'inline-flex items-center justify-center gap-1.5 px-4 py-2 rounded-lg font-semibold text-sm ' +
  'transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 ' +
  'focus-visible:ring-offset-1 dark:focus-visible:ring-offset-gray-800';

const Field = ({ label, help, children }) => (
  <div className="flex flex-col min-w-0 my-2">
    <label className={labelCls}>
      {label}
      {help && (
        <span title={help} className="ml-1 inline-flex align-middle cursor-help">
          <InformationCircleIcon className="h-3.5 w-3.5 text-primary-500 dark:text-primary-400" />
        </span>
      )}
    </label>
    {children}
  </div>
);

Field.propTypes = {
  label: PropTypes.node,
  help: PropTypes.string,
  children: PropTypes.node,
};

const Fieldset = ({ legend, children }) => (
  <fieldset className="border border-gray-200 dark:border-gray-700 rounded-lg px-3 pb-3 pt-1 my-3 min-w-0">
    <legend className="text-xs font-semibold text-primary-700 dark:text-primary-400 uppercase tracking-wide px-1.5">
      {legend}
    </legend>
    {children}
  </fieldset>
);

Fieldset.propTypes = {
  legend: PropTypes.node,
  children: PropTypes.node,
};

// KPI — todas las tarjetas con el mismo peso visual (sin jerarquía "hero").
const Kpi = ({ label, value, note, tone }) => {
  const valueTone =
    tone === 'bad'
      ? 'text-red-600 dark:text-red-400'
      : tone === 'good'
      ? 'text-green-700 dark:text-green-400'
      : 'text-primary-700 dark:text-primary-300';
  return (
    <div className="bg-gradient-to-b from-white to-gray-50 dark:from-gray-700/60 dark:to-gray-700/60 border border-gray-200 dark:border-gray-600 rounded-lg px-4 py-3">
      <div className="text-[0.7rem] font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
        {label}
      </div>
      <div className={`text-2xl font-bold mt-1 tabular-nums ${valueTone}`}>{value}</div>
      {note && (
        <div className="text-[0.72rem] text-gray-500 dark:text-gray-400 mt-1 leading-snug">
          {note}
        </div>
      )}
    </div>
  );
};

Kpi.propTypes = {
  label: PropTypes.node,
  value: PropTypes.node,
  note: PropTypes.node,
  tone: PropTypes.string,
};

// ===== Sistema de clases de tablas =====
// border-separate + spacing-0: imprescindible para que las celdas `sticky`
// conserven fondo y bordes al hacer scroll.
const GRP_TINT = {
  irph: 'bg-red-50 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  a: 'bg-green-50 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  b: 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
  sob: 'bg-amber-50 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300',
  acum: 'bg-primary-50 text-primary-700 dark:bg-primary-900/30 dark:text-primary-300',
  neutral: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-200',
};
const GRP_BASE =
  'px-3 h-9 text-[0.7rem] font-bold uppercase tracking-wide text-center ' +
  'border-b border-gray-200 dark:border-gray-600 sticky top-0 z-20';
const grpHead = (tint, start) =>
  `${GRP_BASE} ${GRP_TINT[tint]}` +
  (start ? ' border-l-2 border-l-gray-300 dark:border-l-gray-500' : '');

const DET_BASE =
  'px-3 py-2 text-right whitespace-nowrap text-[0.72rem] font-semibold ' +
  'text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 ' +
  'border-b-2 border-gray-200 dark:border-gray-600';
// Cabecera de detalle bajo la fila de grupos (tablas de 2 niveles).
const detHead = (start) =>
  `${DET_BASE} sticky top-9 z-20` +
  (start ? ' border-l-2 border-l-gray-300 dark:border-l-gray-500' : '');
// Cabecera única (tablas de 1 nivel: serie de índices).
const soloHead = (start) =>
  `${DET_BASE} sticky top-0 z-20` +
  (start ? ' border-l-2 border-l-gray-300 dark:border-l-gray-500' : '');

// Esquina fija (columna Mes/Año) — cubre toda la altura de la cabecera.
const cornerHead =
  'px-3 pb-2 text-left align-bottom text-[0.72rem] font-bold uppercase tracking-wide ' +
  'text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 ' +
  'border-b-2 border-gray-200 dark:border-gray-600 sticky left-0 top-0 z-30';

const DATA_BASE =
  'px-3 py-1.5 text-right whitespace-nowrap tabular-nums ' +
  'border-b border-gray-100 dark:border-gray-700/50';
const dataCell = (start) =>
  DATA_BASE + (start ? ' border-l-2 border-l-gray-200 dark:border-l-gray-600' : '');

// Celda de la columna fija Mes/Año.
const MES_BASE =
  'px-3 py-1.5 text-left whitespace-nowrap font-medium sticky left-0 z-10 ' +
  'border-b border-gray-100 dark:border-gray-700/50';
const mesCell = (rowBg) =>
  `${MES_BASE} ${rowBg} group-hover:bg-primary-50 dark:group-hover:bg-gray-700/70`;

const rowBgFor = (di, isYearEnd) =>
  isYearEnd
    ? 'bg-amber-50 dark:bg-amber-950'
    : di % 2
    ? 'bg-gray-50 dark:bg-gray-900'
    : 'bg-white dark:bg-gray-800';

const ScrollTable = ({ children }) => (
  <div className="overflow-auto rounded-lg border border-gray-200 dark:border-gray-700 max-h-[76vh]">
    <table className="w-full border-separate border-spacing-0 text-[0.8rem]">{children}</table>
  </div>
);
ScrollTable.propTypes = { children: PropTypes.node };

const deltaCls = (n) =>
  n >= 0
    ? ' text-red-600 dark:text-red-400 font-semibold'
    : ' text-green-600 dark:text-green-400 font-semibold';

export const CalculadoraIRPH = () => {
  const { t } = useTranslation();
  const [form, setForm] = useState(FORM_INICIAL);
  const [sim, setSim] = useState(null);
  const [resTab, setResTab] = useState('cuadro');
  const [soloCierre, setSoloCierre] = useState(false);
  const [status, setStatus] = useState({ msg: '', kind: 'ok' });
  const [stale, setStale] = useState(false);

  // Serie de índices: arranca con el estático bundleado (render instantáneo,
  // funcionamiento offline) y se sobrescribe en cuanto Supabase responde.
  const [serie, setSerie] = useState(SERIE_ESTATICA);
  // 'loading' mientras se pide a Supabase; 'live' si llegó OK; 'offline' si falló.
  const [serieStatus, setSerieStatus] = useState('loading');

  const set = (campo, valor) => {
    setForm((f) => ({ ...f, [campo]: valor }));
    setStale(true);
  };

  const calcular = (formActual = form, serieActual = serie) => {
    try {
      const s = ejecutarSimulacion(formActual, serieActual);
      setSim(s);
      const now = new Date();
      setStatus({
        msg: `${t('Calculado a las')} ${now.toLocaleTimeString('es-ES')} · ${
          s.filasIRPH.length
        } ${t('cuotas')}`,
        kind: 'ok',
      });
      setStale(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error('Error en calcular():', err);
      setStatus({ msg: `${t('Error')}: ${err.message}`, kind: 'err' });
    }
  };

  // Al montar: (1) render inmediato con el estático para no bloquear,
  //            (2) en paralelo pide la serie viva a Supabase y recalcula.
  useEffect(() => {
    calcular(FORM_INICIAL, SERIE_ESTATICA);
    let cancelado = false;
    (async () => {
      try {
        const { data, error } = await supabaseClient
          .from('irph_serie')
          .select('ym, ce, cajas, bancos, eur, mibor');
        if (cancelado) return;
        if (error) throw error;
        if (!Array.isArray(data) || data.length === 0)
          throw new Error('Supabase devolvió 0 filas');
        // BD gana sobre el estático (correcciones + meses nuevos).
        const dbDict = Object.fromEntries(data.map((r) => [r.ym, r]));
        const merged = { ...SERIE_ESTATICA, ...dbDict };
        setSerie(merged);
        setSerieStatus('live');
        // Recalcular con datos vivos sin pisar lo que el usuario haya tecleado.
        calcular(form, merged);
      } catch (err) {
        if (cancelado) return;
        // eslint-disable-next-line no-console
        console.warn('Supabase irph_serie no disponible, usando datos locales:', err.message);
        setSerieStatus('offline');
      }
    })();
    return () => {
      cancelado = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const cargarEjemplo = () => {
    setForm(FORM_INICIAL);
    calcular(FORM_INICIAL);
  };

  // ----- Amortizaciones anticipadas -----
  const addAnticipo = () =>
    set('anticipos', [...form.anticipos, { mes: '', imp: '', efecto: 'cuota' }]);
  const updAnticipo = (idx, campo, valor) =>
    set(
      'anticipos',
      form.anticipos.map((a, i) => (i === idx ? { ...a, [campo]: valor } : a))
    );
  const delAnticipo = (idx) =>
    set(
      'anticipos',
      form.anticipos.filter((_, i) => i !== idx)
    );

  // ----- Datos derivados de la simulación -----
  const derivados = useMemo(() => {
    if (!sim) return null;
    const { filasIRPH, filasAlt, filasAltB, opcsBase, diff, diffAlt, diffAltB, altBEnabled } = sim;
    const altBOn = altBEnabled && filasAltB;
    let corteIdx = Math.min(filasIRPH.length, filasAlt.length);
    if (altBOn) corteIdx = Math.min(corteIdx, filasAltB.length);

    const tI = totales(filasIRPH, corteIdx);
    const tA = totales(filasAlt, corteIdx);
    const tB = altBOn ? totales(filasAltB, corteIdx) : null;
    const sobA = tI.int - tA.int;
    const sobB = tB ? tI.int - tB.int : null;
    const taeI = calcularTAEAproximada(opcsBase.capital, filasIRPH.slice(0, corteIdx));
    const taeA = calcularTAEAproximada(opcsBase.capital, filasAlt.slice(0, corteIdx));
    const taeB = tB ? calcularTAEAproximada(opcsBase.capital, filasAltB.slice(0, corteIdx)) : null;
    const fLastI = filasIRPH[corteIdx - 1];
    const anual = agregarAnual(filasIRPH, filasAlt, filasAltB, corteIdx, altBOn);

    const lblAltA = `${LBL_INDICE[sim.indAlt] || sim.indAlt} + ${diffAlt}%`;
    const lblAltB = tB ? `${LBL_INDICE[sim.indAltB] || sim.indAltB} + ${diffAltB}%` : '';

    return {
      altBOn, corteIdx, tI, tA, tB, sobA, sobB, taeI, taeA, taeB,
      fLastI, anual, lblAltA, lblAltB, opcsBase, diff,
    };
  }, [sim]);

  // Filas visibles del cuadro mes a mes (aplica el filtro "solo cierre de año").
  const filasCuadro = useMemo(() => {
    if (!sim || !derivados) return [];
    const out = [];
    for (let i = 0; i < derivados.corteIdx; i++) {
      const a = sim.filasIRPH[i];
      const b = sim.filasAlt[i];
      const c = derivados.altBOn ? sim.filasAltB[i] : null;
      const isYearEnd = a.fecha.getMonth() === 11;
      if (soloCierre && !isYearEnd && i !== derivados.corteIdx - 1) continue;
      out.push({ a, b, c, isYearEnd });
    }
    return out;
  }, [sim, derivados, soloCierre]);

  // ----- Convivencia 1999 (Euríbor vs MIBOR) -----
  const conv1999 = useMemo(() => {
    const vacio = { rows: [], sobreE: 0, sobreM: 0 };
    if (!sim) return vacio;
    const cubre1999 = sim.filasIRPH.some((f) => f.k.startsWith('1999'));
    if (!cubre1999) return vacio;
    const o = sim.opcsBase;
    const diffA = sim.diffAlt;
    const filasE = simular({ ...o, indice: 'EUR_MIBOR', diff: diffA });
    const filasM = simular({ ...o, indice: 'MIBOR_EUR', diff: diffA });
    const n = Math.min(sim.filasIRPH.length, filasE.length, filasM.length);
    const rows = [];
    let irphInt = 0;
    let eInt = 0;
    let mInt = 0;
    for (let i = 0; i < n; i++) {
      irphInt += sim.filasIRPH[i].interes;
      eInt += filasE[i].interes;
      mInt += filasM[i].interes;
      if (filasE[i].k.startsWith('1999')) {
        rows.push({
          k: filasE[i].k,
          eTipo: filasE[i].tipo, eInt: filasE[i].interes,
          mTipo: filasM[i].tipo, mInt: filasM[i].interes,
        });
      }
    }
    // sobreE/sobreM = sobrecoste TOTAL del préstamo según se calcule 1999 con
    // Euríbor o con MIBOR (el resto del préstamo es idéntico en ambos).
    return { rows, sobreE: irphInt - eInt, sobreM: irphInt - mInt };
  }, [sim]);

  // ----- Exportación CSV -----
  const exportarCSV = () => {
    if (!sim) return;
    const { filasIRPH, filasAlt, filasAltB } = sim;
    const altBOn = sim.altBEnabled && filasAltB;
    let corteIdx = Math.min(filasIRPH.length, filasAlt.length);
    if (altBOn) corteIdx = Math.min(corteIdx, filasAltB.length);
    const head = [
      'Mes', 'Tipo IRPH %', 'Cuota IRPH', 'Interes IRPH', 'Capital IRPH', 'Saldo IRPH',
      'Tipo Alt.A %', 'Cuota Alt.A', 'Interes Alt.A', 'Capital Alt.A', 'Saldo Alt.A', 'Sobrecoste A',
    ];
    if (altBOn)
      head.push('Tipo Alt.B %', 'Cuota Alt.B', 'Interes Alt.B', 'Capital Alt.B', 'Saldo Alt.B', 'Sobrecoste B');
    let csv = head.join(';') + '\n';
    for (let i = 0; i < corteIdx; i++) {
      const a = filasIRPH[i];
      const b = filasAlt[i];
      const c = altBOn ? filasAltB[i] : null;
      const row = [
        a.k, a.tipo.toFixed(4), a.cuota.toFixed(2), a.interes.toFixed(2),
        (a.amort + (a.extra || 0)).toFixed(2), a.saldo.toFixed(2),
        b.tipo.toFixed(4), b.cuota.toFixed(2), b.interes.toFixed(2),
        (b.amort + (b.extra || 0)).toFixed(2), b.saldo.toFixed(2),
        (a.interes - b.interes).toFixed(2),
      ];
      if (c)
        row.push(
          c.tipo.toFixed(4), c.cuota.toFixed(2), c.interes.toFixed(2),
          (c.amort + (c.extra || 0)).toFixed(2), c.saldo.toFixed(2),
          (a.interes - c.interes).toFixed(2)
        );
      csv += row.join(';') + '\n';
    }
    const nombre =
      'cuadro_irph_' +
      (form.cliente || 'sin_cliente').replace(/[^a-z0-9_-]+/gi, '_') +
      '.csv';
    saveAs(new Blob([csv], { type: 'text/csv;charset=utf-8;' }), nombre);
  };

  // ----- Exportación PDF (jsPDF cargado de forma diferida) -----
  const exportarPDF = async () => {
    if (!sim || !derivados) return;
    const { jsPDF } = await import('jspdf');
    const { filasIRPH, filasAlt, filasAltB } = sim;
    const altBOn = derivados.altBOn;
    const corteIdx = derivados.corteIdx;
    const doc = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });
    const W = 210;
    const H = 297;
    const M = 15;

    doc.setFillColor(16, 58, 91);
    doc.rect(0, 0, W, 28, 'F');
    doc.setTextColor(255, 255, 255);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(16);
    doc.text('COVENTIA LEGAL', M, 14);
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    doc.text('Informe pericial — Cuantificación de sobrecoste IRPH', M, 21);
    doc.setFontSize(8);
    doc.text('Fecha emisión: ' + new Date().toLocaleDateString('es-ES'), W - M, 21, {
      align: 'right',
    });
    doc.setFillColor(160, 122, 44);
    doc.rect(0, 28, W, 1.5, 'F');

    doc.setTextColor(22, 33, 46);
    let y = 40;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('1. Datos del expediente', M, y);
    y += 6;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const lblA = derivados.lblAltA;
    const lblB = altBOn ? derivados.lblAltB : '';
    const lines = [
      'Cliente / expediente: ' + (form.cliente || '(sin identificar)'),
      'Capital inicial: ' + eur(parseFloat(form.capital)),
      'Plazo: ' + form.plazo + ' meses · Firma: ' + form.fechaFirma,
      'Índice contratado: ' + (LBL_INDICE[form.indiceIRPH] || form.indiceIRPH) + ' + ' + form.diffIRPH + '%',
      'Revisión: cada ' + form.periodRev + ' meses (offset ' + form.offsetRev + ')',
      'Redondeo: ' + (LBL_REDONDEO[form.redondeo] || form.redondeo) + (form.redondeoAlza ? ' (al alza)' : ''),
      'Tratamiento tras supresión IRPH (2013): ' + (LBL_SUPRESION[form.trasSupresion] || form.trasSupresion),
      'Escenario alternativo A: ' + lblA,
      altBOn ? 'Escenario alternativo B: ' + lblB : null,
      'Fecha de corte del cálculo: ' + form.fechaCorte,
    ].filter(Boolean);
    for (const l of lines) {
      doc.text(l, M, y);
      y += 4.5;
    }

    y += 4;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('2. Resultados', M, y);
    y += 6;

    let totI = 0;
    let totA = 0;
    let totB = 0;
    let capI = 0;
    let capA = 0;
    let capB = 0;
    for (let i = 0; i < corteIdx; i++) {
      totI += filasIRPH[i].interes;
      totA += filasAlt[i].interes;
      capI += filasIRPH[i].amort + (filasIRPH[i].extra || 0);
      capA += filasAlt[i].amort + (filasAlt[i].extra || 0);
      if (altBOn) {
        totB += filasAltB[i].interes;
        capB += filasAltB[i].amort + (filasAltB[i].extra || 0);
      }
    }
    const sobA = totI - totA;
    const sobB = altBOn ? totI - totB : null;
    const saldoI = filasIRPH[corteIdx - 1]?.saldo ?? 0;
    const saldoA = filasAlt[corteIdx - 1]?.saldo ?? 0;
    const saldoB = altBOn ? filasAltB[corteIdx - 1]?.saldo ?? 0 : null;

    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    const kpis = [
      ['Mensualidades analizadas', corteIdx + ''],
      ['Intereses pagados con IRPH', eur(totI)],
      ['Intereses pagados con escenario alternativo A', eur(totA)],
      altBOn ? ['Intereses pagados con escenario alternativo B', eur(totB)] : null,
      ['SOBRECOSTE vs Alt. A (reclamación principal)', eur(sobA)],
      // Horquilla 1999 si el préstamo toca el preciodo pre-2000
      conv1999.rows.length > 0 && sim.indAlt === 'EUR_MIBOR'
        ? ['Horquilla 1999 — Sobrecoste A si 1999 = Euríbor', eur(conv1999.sobreE)]
        : null,
      conv1999.rows.length > 0 && sim.indAlt === 'EUR_MIBOR'
        ? ['Horquilla 1999 — Sobrecoste A si 1999 = MIBOR', eur(conv1999.sobreM)]
        : null,
      altBOn ? ['SOBRECOSTE vs Alt. B (sensibilidad)', eur(sobB)] : null,
      altBOn ? ['Diferencia de sensibilidad (A − B)', eur(sobA - sobB)] : null,
      ['Capital amortizado IRPH', eur(capI)],
      ['Capital amortizado Alt. A', eur(capA)],
      altBOn ? ['Capital amortizado Alt. B', eur(capB)] : null,
      ['Capital pendiente IRPH a fecha de corte', eur(saldoI)],
      ['Capital pendiente Alt. A a fecha de corte', eur(saldoA)],
      altBOn ? ['Capital pendiente Alt. B a fecha de corte', eur(saldoB)] : null,
      ['Diferencia de capital pendiente (IRPH − Alt. A)', eur(saldoI - saldoA)],
      altBOn ? ['Diferencia de capital pendiente (IRPH − Alt. B)', eur(saldoI - saldoB)] : null,
    ].filter(Boolean);
    for (const [k, v] of kpis) {
      doc.setFont('helvetica', 'normal');
      doc.text(k + ':', M, y);
      doc.setFont('helvetica', 'bold');
      doc.text(v, W - M, y, { align: 'right' });
      y += 5;
      if (y > H - 25) {
        doc.addPage();
        y = 20;
      }
    }

    y += 2;
    if (y > H - 40) {
      doc.addPage();
      y = 20;
    }
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('3. Conclusión y análisis de sensibilidad', M, y);
    y += 5;
    doc.setFontSize(9);
    doc.setFont('helvetica', 'normal');
    let concl =
      'El sobrecoste cuantificado por la aplicación del índice IRPH frente al escenario alternativo A (' +
      lblA +
      ') asciende a ' +
      eur(sobA) +
      ' en el periodo analizado (' +
      corteIdx +
      ' mensualidades).';
    if (altBOn) {
      concl +=
        ' Al simular el escenario alternativo B (' +
        lblB +
        '), el sobrecoste sería de ' +
        eur(sobB) +
        ', lo que arroja una diferencia de sensibilidad de ' +
        eur(sobA - sobB) +
        ' entre ambos escenarios. Esta horquilla permite al juzgador y a las partes valorar el impacto económico del criterio de sustitución elegido.';
    }
    if (conv1999.rows.length > 0 && sim.indAlt === 'EUR_MIBOR') {
      concl +=
        ' Dado que el préstamo abarca 1999, único año en que el Euríbor y el MIBOR coexistieron como referencia, se ofrece la horquilla de sensibilidad de dicho ejercicio: el sobrecoste asciende a ' +
        eur(conv1999.sobreE) +
        ' si 1999 se calcula con Euríbor y a ' +
        eur(conv1999.sobreM) +
        ' si se calcula con MIBOR (diferencia de ' +
        eur(conv1999.sobreM - conv1999.sobreE) +
        ').';
    }
    concl +=
      ' Procede el ejercicio de la acción de nulidad por falta de transparencia de la cláusula de referencia, conforme a la doctrina del TJUE (SSTJUE C-125/18, C-265/22 y C-300/23), con restitución de las cantidades cobradas en exceso.';
    const spl = doc.splitTextToSize(concl, W - 2 * M);
    doc.text(spl, M, y);
    y += spl.length * 4.5 + 2;

    doc.addPage('a4', altBOn ? 'landscape' : 'portrait');
    const WL = altBOn ? 297 : 210;
    const HL = altBOn ? 210 : 297;
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('4. Desglose anual' + (altBOn ? ' (Alt. A vs Alt. B)' : ''), M, 20);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    let yy = 28;
    const header = altBOn
      ? ['Año', 'Int.IRPH', 'Int.AltA', 'Int.AltB', 'Cap.IRPH', 'Cap.AltA', 'Cap.AltB', 'Sobrec.A', 'Sobrec.B', 'Acum.A', 'Acum.B']
      : ['Año', 'Int. IRPH', 'Int. Alt.', 'Cap. IRPH', 'Cap. Alt.', 'Sobrecoste', 'Acumulado'];
    const usable = WL - 2 * M;
    const colW = usable / header.length;
    const colsX = header.map((_, i) => M + colW * i);
    doc.setFont('helvetica', 'bold');
    for (let i = 0; i < header.length; i++) doc.text(header[i], colsX[i], yy);
    yy += 4;
    doc.setLineWidth(0.2);
    doc.line(M, yy, WL - M, yy);
    yy += 3;
    doc.setFont('helvetica', 'normal');
    for (const a of derivados.anual) {
      const row = altBOn
        ? [a.year, eur(a.intI), eur(a.intA), eur(a.intB), eur(a.capI), eur(a.capA), eur(a.capB), eur(a.sA), eur(a.sB), eur(a.acumA), eur(a.acumB)]
        : [a.year, eur(a.intI), eur(a.intA), eur(a.capI), eur(a.capA), eur(a.sA), eur(a.acumA)];
      for (let i = 0; i < row.length; i++) doc.text(row[i], colsX[i], yy);
      yy += 4.2;
      if (yy > HL - 20) {
        doc.addPage('a4', altBOn ? 'landscape' : 'portrait');
        yy = 20;
      }
    }

    doc.addPage();
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('5. Marco jurisprudencial', M, 20);
    doc.setFontSize(8);
    doc.setFont('helvetica', 'normal');
    const txt = [
      'STJUE 3 marzo 2020 (C-125/18, Gómez del Moral/Bankia): el IRPH es susceptible de control de transparencia.',
      'STJUE 13 julio 2023 (C-265/22, Banco Santander): es pertinente la Circular 5/1994 BdE, que advertía de la necesidad de aplicar un diferencial negativo al IRPH.',
      'STJUE 12 diciembre 2024 (C-300/23): reitera y aclara los criterios sobre las consecuencias económicas significativas.',
      'STS Pleno 1590/2025 y 1591/2025, 11/11/2025: doctrina actual del TS, cuestionada por nuevas cuestiones prejudiciales (Tribunal Instancia Palma 11/03/2026).',
      'Normativa: Circular 8/1990, 5/1994 y 5/2012 BdE; Orden EHA/2899/2011; Ley 14/2013 DA 15ª; Directiva 93/13/CEE.',
    ];
    let yt = 28;
    for (const tx of txt) {
      const s = doc.splitTextToSize('• ' + tx, 210 - 2 * M);
      doc.text(s, M, yt);
      yt += s.length * 4.2 + 1;
    }
    yt += 3;
    doc.setFont('helvetica', 'italic');
    doc.text(
      'Informe emitido como herramienta pericial de cuantificación. Datos de índices: Banco de España / BOE.',
      M,
      yt
    );

    const pages = doc.internal.pages.length - 1;
    for (let p = 1; p <= pages; p++) {
      doc.setPage(p);
      doc.setFontSize(7);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(120, 120, 120);
      const pw = doc.internal.pageSize.getWidth();
      const ph = doc.internal.pageSize.getHeight();
      doc.text(
        'Coventia Legal · Calculadora IRPH v2026.04 · Página ' + p + ' de ' + pages,
        pw / 2,
        ph - 8,
        { align: 'center' }
      );
    }
    doc.save(
      'Informe_IRPH_' +
        (form.cliente || 'sin_cliente').replace(/[^a-z0-9_-]+/gi, '_') +
        '.pdf'
    );
  };

  const tabBtn = (id, label) => (
    <button
      key={id}
      onClick={() => setResTab(id)}
      className={
        'px-4 py-2.5 text-sm font-medium border-b-2 -mb-px transition-colors whitespace-nowrap ' +
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 rounded-t ' +
        (resTab === id
          ? 'text-primary-700 dark:text-primary-300 border-primary-600'
          : 'text-gray-500 dark:text-gray-400 border-transparent hover:text-gray-700 dark:hover:text-gray-200')
      }
    >
      {label}
    </button>
  );

  // Serie histórica completa
  const serieRows = useMemo(
    () => Object.keys(serie).sort().reverse(),
    [serie]
  );

  const altB = derivados?.altBOn;
  // Convivencia inline: si algún escenario usa el índice "mercado continuo" Y
  // el préstamo toca el periodo pre-2000, mostramos en el cuadro el valor
  // mensual de Euríbor y MIBOR (– donde no haya dato: Euríbor antes de 1999,
  // MIBOR desde 2000). En préstamos solo-Euríbor (≥2000) no se muestran, porque
  // el MIBOR saldría siempre a "–" y no aportarían nada.
  const cubrePre2000 = !!sim && sim.filasIRPH.some((f) => f.k < '2000-01');
  const verConviv =
    cubrePre2000 && (sim.indAlt === 'EUR_MIBOR' || (sim.altBEnabled && sim.indAltB === 'EUR_MIBOR'));
  // Horquilla 1999 en el titular: solo si el escenario A (reclamación principal)
  // usa "mercado continuo" y el préstamo cubre 1999 (es cuando el titular tiene
  // dos lecturas según el índice de ese año).
  const horquilla1999 = !!sim && sim.indAlt === 'EUR_MIBOR' && conv1999.rows.length > 0;

  return (
    <div className="w-full">
      {/* Intro */}
      <div className="mb-5">
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          {t('Calculadora de sobrecoste por cláusula IRPH')}
        </h1>
        <p className="text-sm text-gray-600 dark:text-gray-400 mt-1 max-w-4xl">
          {t(
            'Herramienta pericial para cuantificar la diferencia entre lo pagado con IRPH y lo que el cliente habría pagado con Euríbor 12m. Cálculo mes a mes en sistema francés, con tratamiento legal de la supresión del IRPH Cajas/Bancos (DA 15ª Ley 14/2013), redondeos, cláusulas suelo/techo, carencia y amortizaciones parciales.'
          )}
        </p>
        <div className="flex flex-wrap gap-1.5 mt-2.5">
          {[
            'Sistema francés mes a mes',
            'Migración legal IRPH 2013',
            'Redondeo configurable',
            'Suelo / techo',
            'Amortizaciones anticipadas',
            'Fecha de corte',
            'Informe PDF',
          ].map((p) => (
            <span
              key={p}
              className="text-[0.72rem] bg-gray-100 dark:bg-gray-700 text-primary-700 dark:text-primary-300 px-2.5 py-1 rounded-full border border-gray-200 dark:border-gray-600"
            >
              {t(p)}
            </span>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,400px)_1fr] gap-4 items-start">
        {/* ===== COLUMNA IZQUIERDA: PARÁMETROS ===== */}
        <aside className="min-w-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4">
          <h2 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
            {t('Datos del préstamo')}
          </h2>

          <Fieldset legend={t('Identificación')}>
            <Field label={t('Cliente / Expediente')}>
              <input
                className={inputCls}
                type="text"
                value={form.cliente}
                onChange={(e) => set('cliente', e.target.value)}
                placeholder="Ej.: García Ruiz — IRPH/2026/045"
              />
            </Field>
          </Fieldset>

          <Fieldset legend={t('Préstamo original')}>
            <div className="grid grid-cols-2 gap-2.5">
              <Field label={t('Capital inicial (€)')}>
                <input className={inputCls} type="number" step="0.01" value={form.capital} onChange={(e) => set('capital', e.target.value)} />
              </Field>
              <Field label={t('Plazo (meses)')}>
                <input className={inputCls} type="number" step="1" min="1" max="1200" value={form.plazo} onChange={(e) => set('plazo', e.target.value)} />
              </Field>
            </div>
            <div className="grid grid-cols-2 gap-2.5">
              <Field label={t('Fecha de firma')}>
                <input className={inputCls} type="month" min={MES_MIN} max={MES_MAX} value={form.fechaFirma} onChange={(e) => set('fechaFirma', e.target.value)} />
              </Field>
              <Field label={t('Periodicidad de revisión')}>
                <select className={inputCls} value={form.periodRev} onChange={(e) => set('periodRev', e.target.value)}>
                  <option value="12">{t('Anual')}</option>
                  <option value="6">{t('Semestral')}</option>
                  <option value="3">{t('Trimestral')}</option>
                  <option value="1">{t('Mensual')}</option>
                </select>
              </Field>
            </div>
            <Field
              label={t('Mes de revisión (offset frente a firma)')}
              help={t('Mes utilizado por el banco para tomar el valor del índice. 0 = mismo mes; -1 = mes anterior (lo más común); -2 = dos meses antes')}
            >
              <select className={inputCls} value={form.offsetRev} onChange={(e) => set('offsetRev', e.target.value)}>
                <option value="-2">{t('Dos meses antes (mes -2)')}</option>
                <option value="-1">{t('Mes anterior (mes -1) — habitual')}</option>
                <option value="0">{t('Mismo mes de revisión')}</option>
              </select>
            </Field>
            <Field label={t('Carencia inicial (meses, sólo intereses)')}>
              <input className={inputCls} type="number" step="1" min="0" value={form.carencia} onChange={(e) => set('carencia', e.target.value)} />
            </Field>
          </Fieldset>

          <Fieldset legend={t('Índice y diferencial reales (IRPH)')}>
            <div className="grid grid-cols-2 gap-2.5">
              <Field
                label={t('Índice original')}
                help={t('El MIBOR fue el índice oficial de las hipotecas a tipo variable hasta la llegada del euro: solo válido como referencia contractual para préstamos firmados antes del 1/1/2000. El Euríbor se publica desde enero de 1999.')}
              >
                <select className={inputCls} value={form.indiceIRPH} onChange={(e) => set('indiceIRPH', e.target.value)}>
                  <option value="IRPH_CE">IRPH Conjunto Entidades</option>
                  <option value="IRPH_CAJAS">IRPH Cajas (hasta oct 2013)</option>
                  <option value="IRPH_BANCOS">IRPH Bancos (hasta oct 2013)</option>
                  <option value="MIBOR">MIBOR (anterior a 2000)</option>
                </select>
              </Field>
              <Field label={t('Diferencial original (%)')}>
                <input className={inputCls} type="number" step="0.001" value={form.diffIRPH} onChange={(e) => set('diffIRPH', e.target.value)} />
              </Field>
            </div>
            <Field
              label={t('Tipo inicial fijo durante (meses)')}
              help={t('Meses iniciales en los que el tipo fue fijo antes de pasar a variable. 0 si fue variable desde el origen.')}
            >
              <input className={inputCls} type="number" step="1" min="0" value={form.tipoFijoMeses} onChange={(e) => set('tipoFijoMeses', e.target.value)} />
            </Field>
            <Field label={t('Tipo fijo inicial (%) (si procede)')}>
              <input className={inputCls} type="number" step="0.001" value={form.tipoFijoValor} onChange={(e) => set('tipoFijoValor', e.target.value)} />
            </Field>
            <div className="grid grid-cols-3 gap-2">
              <Field label={t('Suelo (%)')}>
                <input className={inputCls} type="number" step="0.001" value={form.suelo} onChange={(e) => set('suelo', e.target.value)} placeholder="ej. 2,5" />
              </Field>
              <Field label={t('Techo (%)')}>
                <input className={inputCls} type="number" step="0.001" value={form.techo} onChange={(e) => set('techo', e.target.value)} placeholder="ej. 12" />
              </Field>
              <Field label={t('Redondeo')}>
                <select className={inputCls} value={form.redondeo} onChange={(e) => set('redondeo', e.target.value)}>
                  <option value="0">{t('Ninguno')}</option>
                  <option value="0.25">{t('Al cuartillo (0,25)')}</option>
                  <option value="0.125">{t('Al octavo (0,125)')}</option>
                  <option value="0.05">{t('A 0,05')}</option>
                  <option value="0.01">{t('A 0,01')}</option>
                </select>
              </Field>
            </div>
            <label className="flex items-center gap-2 my-1.5 text-sm text-gray-700 dark:text-gray-300">
              <input type="checkbox" className="w-4 h-4 accent-primary-600" checked={form.redondeoAlza} onChange={(e) => set('redondeoAlza', e.target.checked)} />
              {t('Redondeo al alza (si NO marcado, redondeo al más próximo)')}
            </label>
          </Fieldset>

          <Fieldset legend={t('Tratamiento IRPH Cajas/Bancos tras 1/11/2013')}>
            <Field label={t('Comportamiento')}>
              <select className={inputCls} value={form.trasSupresion} onChange={(e) => set('trasSupresion', e.target.value)}>
                <option value="DA15">{t('Migración legal a IRPH-CE (DA 15ª L.14/2013)')}</option>
                <option value="congelar">{t('Congelar último valor publicado (oct/2013)')}</option>
                <option value="euribor">{t('Sustituir por Euríbor + diferencial original')}</option>
                <option value="fijo">{t('Convertir a tipo fijo (último IRPH + diferencial)')}</option>
              </select>
            </Field>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('La DA 15ª de la L.14/2013 dispuso la sustitución por IRPH-CE más diferencial. Esta calculadora aplica IRPH-CE + diferencial original, conforme al criterio mayoritario en la jurisprudencia menor.')}
            </p>
          </Fieldset>

          <Fieldset legend={t('Escenario alternativo A (reclamación principal)')}>
            <div className="grid grid-cols-2 gap-2.5">
              <Field
                label={t('Índice')}
                help={t('Para préstamos firmados antes de 1999 el Euríbor aún no existía y el motor calcularía esos meses al 0 %, inflando el sobrecoste. Usa "Euríbor / MIBOR (mercado continuo)": reconstruye el escenario con MIBOR hasta dic/1998 y Euríbor desde ene/1999.')}
              >
                <select className={inputCls} value={form.indiceAlt} onChange={(e) => set('indiceAlt', e.target.value)}>
                  <option value="EURIBOR12">{t('Euríbor 12 meses')}</option>
                  <option value="EUR_MIBOR">{t('Euríbor / MIBOR (mercado continuo)')}</option>
                  <option value="MIBOR">{t('MIBOR')}</option>
                  <option value="NULO">{t('Sin índice (sólo diferencial)')}</option>
                </select>
              </Field>
              <Field label={t('Diferencial (%)')}>
                <input className={inputCls} type="number" step="0.001" value={form.diffAlt} onChange={(e) => set('diffAlt', e.target.value)} />
              </Field>
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400">
              {t('Criterio TJUE/TS más favorable: Euríbor + 0% (eliminación cláusula sin sustitución). Para hipotecas anteriores a 2000, el escenario de mercado parte del MIBOR.')}
            </p>
          </Fieldset>

          <Fieldset legend={t('Escenario alternativo B (sensibilidad)')}>
            <label className="flex items-center gap-2 my-1.5 text-sm text-gray-700 dark:text-gray-300">
              <input type="checkbox" className="w-4 h-4 accent-primary-600" checked={form.altBEnabled} onChange={(e) => set('altBEnabled', e.target.checked)} />
              {t('Activar segundo escenario')}
            </label>
            <div className="grid grid-cols-2 gap-2.5">
              <Field
                label={t('Índice')}
                help={t('Para contrastar Euríbor y MIBOR (coexisten en la serie de 1999 a 2020): fija el escenario A en "Euríbor / MIBOR (mercado continuo)" y el B en "MIBOR" puro y compara el cálculo real con cada índice.')}
              >
                <select className={inputCls} value={form.indiceAltB} onChange={(e) => set('indiceAltB', e.target.value)} disabled={!form.altBEnabled}>
                  <option value="EURIBOR12">{t('Euríbor 12 meses')}</option>
                  <option value="EUR_MIBOR">{t('Euríbor / MIBOR (mercado continuo)')}</option>
                  <option value="MIBOR">{t('MIBOR')}</option>
                  <option value="NULO">{t('Sin índice (sólo diferencial)')}</option>
                </select>
              </Field>
              <Field label={t('Diferencial (%)')}>
                <input className={inputCls} type="number" step="0.001" value={form.diffAltB} onChange={(e) => set('diffAltB', e.target.value)} disabled={!form.altBEnabled} />
              </Field>
            </div>
          </Fieldset>

          <Fieldset legend={t('Fecha de corte y amortizaciones')}>
            <Field label={t('Calcular sobrecoste hasta (incluido)')}>
              <input className={inputCls} type="month" min={MES_MIN} max={MES_MAX} value={form.fechaCorte} onChange={(e) => set('fechaCorte', e.target.value)} />
            </Field>
            <label className={labelCls}>{t('Amortizaciones parciales anticipadas')}</label>
            <div className="space-y-2">
              {form.anticipos.map((a, idx) => (
                <div key={idx} className="flex items-center gap-1.5">
                  <input className={inputCls + ' !py-1'} type="month" min={MES_MIN} max={MES_MAX} value={a.mes} onChange={(e) => updAnticipo(idx, 'mes', e.target.value)} />
                  <input className={inputCls + ' !py-1'} type="number" step="0.01" placeholder="ej. 5000" value={a.imp} onChange={(e) => updAnticipo(idx, 'imp', e.target.value)} />
                  <select className={inputCls + ' !py-1'} value={a.efecto} onChange={(e) => updAnticipo(idx, 'efecto', e.target.value)}>
                    <option value="cuota">{t('Reducir cuota')}</option>
                    <option value="plazo">{t('Reducir plazo')}</option>
                  </select>
                  <button type="button" onClick={() => delAnticipo(idx)} className="text-red-500 hover:text-red-700 px-1.5 text-lg leading-none">
                    ×
                  </button>
                </div>
              ))}
            </div>
            <button
              type="button"
              onClick={addAnticipo}
              className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              + {t('Añadir amortización')}
            </button>
          </Fieldset>

          {/* Barra de acciones — pegada al fondo de la columna (opaca) */}
          <div className="sticky bottom-0 -mx-4 mt-4 px-4 py-3 bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 shadow-[0_-8px_20px_-12px_rgba(0,0,0,0.22)]">
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => calcular()}
                className={btnBase + ' bg-primary-600 hover:bg-primary-700 text-white'}
              >
                {t('Calcular sobrecoste')}
              </button>
              <button
                type="button"
                onClick={cargarEjemplo}
                className={btnBase + ' border border-primary-600 text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-gray-700'}
              >
                {t('Cargar ejemplo')}
              </button>
              <button
                type="button"
                onClick={exportarPDF}
                className={btnBase + ' bg-amber-600 hover:bg-amber-700 text-white'}
              >
                <DocumentArrowDownIcon className="h-4 w-4" />
                {t('Informe PDF')}
              </button>
              <button
                type="button"
                onClick={exportarCSV}
                className={btnBase + ' border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700'}
              >
                <TableCellsIcon className="h-4 w-4" />
                {t('Descargar CSV')}
              </button>
            </div>
          </div>
        </aside>

        {/* ===== COLUMNA DERECHA: RESULTADOS ===== */}
        <section className="min-w-0 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl p-4 lg:p-5">
          <div className="flex items-center flex-wrap gap-2 mb-1">
            <h2 className="text-lg font-bold text-gray-900 dark:text-white">
              {t('Resumen comparativo')}
            </h2>
            {status.msg && (
              <span
                className={
                  'text-xs px-2.5 py-1 rounded-full border ' +
                  (status.kind === 'err'
                    ? 'text-red-600 bg-red-50 border-red-200 dark:bg-red-900/30'
                    : 'text-green-700 bg-green-50 border-green-200 dark:bg-green-900/30')
                }
              >
                {status.msg}
              </span>
            )}
            {stale && (
              <span className="text-xs px-2.5 py-1 rounded-full border text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-900/30">
                {t('Valores modificados — pulsa "Calcular sobrecoste"')}
              </span>
            )}
            {serieStatus === 'loading' && (
              <span
                className="text-xs px-2.5 py-1 rounded-full border text-gray-600 bg-gray-50 border-gray-200 dark:bg-gray-700/50 dark:text-gray-300 dark:border-gray-600"
                title={t('Solicitando la serie oficial al servidor; entretanto se usan los datos locales bundleados.')}
              >
                {t('Cargando serie oficial…')}
              </span>
            )}
            {serieStatus === 'live' && (
              <span
                className="text-xs px-2.5 py-1 rounded-full border text-primary-700 bg-primary-50 border-primary-200 dark:bg-primary-900/30 dark:text-primary-300 dark:border-primary-800"
                title={t('Serie cargada desde Supabase (origen: Banco de España).')}
              >
                ✓ {t('Datos BdE en vivo')} ({Object.keys(serie).length} {t('meses')})
              </span>
            )}
            {serieStatus === 'offline' && (
              <span
                className="text-xs px-2.5 py-1 rounded-full border text-amber-700 bg-amber-50 border-amber-200 dark:bg-amber-900/30"
                title={t('No se pudo contactar con Supabase; se está usando la serie bundleada localmente como respaldo.')}
              >
                ⚠ {t('Modo offline (datos locales)')}
              </span>
            )}
          </div>
          <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
            {derivados
              ? `${t('Cálculo desde')} ${ym(sim.opcsBase.fechaFirma)} ${t('hasta')} ${
                  derivados.fLastI ? derivados.fLastI.k : '-'
                } · ${derivados.corteIdx} ${t('cuotas analizadas')}.`
              : t('Pulsa "Calcular sobrecoste" para obtener los resultados.')}
          </p>

          {derivados && (
            <>
              {/* KPIs — todos al mismo nivel, como el diseño original */}
              <div className="grid grid-cols-[repeat(auto-fit,minmax(190px,1fr))] gap-2.5 mb-4">
                <Kpi label={t('Sobrecoste IRPH vs Alt. A')} value={eur(derivados.sobA)} note={`${derivados.lblAltA} · ${t('Importe reclamable principal')}`} tone="bad" />
                {horquilla1999 && (
                  <Kpi
                    label={t('Sobrecoste Alt. A · 1999 con MIBOR')}
                    value={eur(conv1999.sobreM)}
                    note={`${t('Horquilla 1999')}: ${eur(conv1999.sobreM - conv1999.sobreE)} ${t('vs criterio Euríbor')}`}
                    tone="bad"
                  />
                )}
                {derivados.tB && (
                  <Kpi label={t('Sobrecoste IRPH vs Alt. B')} value={eur(derivados.sobB)} note={`${derivados.lblAltB} · ${t('Sensibilidad')}`} tone="bad" />
                )}
                {derivados.tB && (
                  <Kpi label={t('Δ Sensibilidad A − B')} value={eur(derivados.sobA - derivados.sobB)} note={t('Diferencia entre ambos escenarios')} />
                )}
                <Kpi label={t('Intereses IRPH')} value={eur(derivados.tI.int)} note={`${pct(derivados.taeI)} ${t('TAE aprox.')}`} />
                <Kpi label={t('Intereses Alt. A')} value={eur(derivados.tA.int)} note={`${pct(derivados.taeA)} ${t('TAE aprox.')}`} tone="good" />
                {derivados.tB && (
                  <Kpi label={t('Intereses Alt. B')} value={eur(derivados.tB.int)} note={`${pct(derivados.taeB)} ${t('TAE aprox.')}`} tone="good" />
                )}
                <Kpi label={t('Cuota actual IRPH')} value={eur(derivados.tI.cuotaUlt)} note={`vs A ${eur(derivados.tA.cuotaUlt)}${derivados.tB ? ' · vs B ' + eur(derivados.tB.cuotaUlt) : ''}`} />
                <Kpi label={t('Capital pendiente IRPH')} value={eur(derivados.tI.saldo)} note={`vs A ${eur(derivados.tA.saldo)}${derivados.tB ? ' · vs B ' + eur(derivados.tB.saldo) : ''}`} />
                <Kpi label={t('Total pagado IRPH')} value={eur(derivados.tI.int + derivados.tI.cap)} note={`vs A ${eur(derivados.tA.int + derivados.tA.cap)}${derivados.tB ? ' · vs B ' + eur(derivados.tB.int + derivados.tB.cap) : ''}`} />
              </div>

              {/* Leyenda */}
              <div className="flex flex-wrap gap-3.5 text-xs text-gray-500 dark:text-gray-400 mb-2">
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-red-500 inline-block" /> IRPH ({t('real')})</span>
                <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-green-600 inline-block" /> {t('Escenario A')}</span>
                {derivados.tB && (
                  <span className="flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-full bg-primary-600 inline-block" /> {t('Escenario B')}</span>
                )}
              </div>

              {/* Sub-pestañas */}
              <div className="flex gap-0.5 border-b border-gray-200 dark:border-gray-700 mb-3 overflow-x-auto">
                {tabBtn('cuadro', t('Cuadro mes a mes'))}
                {tabBtn('anual', t('Desglose anual'))}
                {conv1999.rows.length > 0 && tabBtn('conv1999', t('Convivencia 1999'))}
                {tabBtn('serie', t('Serie de índices'))}
                {tabBtn('jurisp', t('Jurisprudencia'))}
              </div>

              {/* Cuadro mes a mes */}
              {resTab === 'cuadro' && (
                <div>
                  <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      {t('Cuadro mes a mes. Las filas de cierre de año aparecen resaltadas.')}
                    </p>
                    <label className="flex items-center gap-1.5 text-xs text-gray-600 dark:text-gray-300">
                      <input type="checkbox" className="w-4 h-4 accent-primary-600" checked={soloCierre} onChange={(e) => setSoloCierre(e.target.checked)} />
                      {t('Mostrar solo cierre de año')}
                    </label>
                  </div>
                  <ScrollTable>
                    <thead>
                      <tr>
                        <th rowSpan={2} className={cornerHead}>{t('Mes')}</th>
                        <th colSpan={5} className={grpHead('irph', true)}>{t('IRPH (real)')}</th>
                        {verConviv && (
                          <th colSpan={2} className={grpHead('neutral', true)}>{t('Índices (continuo)')}</th>
                        )}
                        <th colSpan={4} className={grpHead('a', true)}>{t('Escenario A')}</th>
                        {altB && <th colSpan={4} className={grpHead('b', true)}>{t('Escenario B')}</th>}
                        <th colSpan={altB ? 2 : 1} className={grpHead('sob', true)}>{t('Sobrecoste')}</th>
                      </tr>
                      <tr>
                        <th className={detHead(true)}>{t('Tipo %')}</th>
                        <th className={detHead()}>{t('Cuota')}</th>
                        <th className={detHead()}>{t('Interés')}</th>
                        <th className={detHead()}>{t('Capital')}</th>
                        <th className={detHead()}>{t('Saldo')}</th>
                        {verConviv && <th className={detHead(true)}>{t('Euríbor')}</th>}
                        {verConviv && <th className={detHead()}>{t('MIBOR')}</th>}
                        <th className={detHead(true)}>{t('Tipo %')}</th>
                        <th className={detHead()}>{t('Cuota')}</th>
                        <th className={detHead()}>{t('Interés')}</th>
                        <th className={detHead()}>{t('Saldo')}</th>
                        {altB && <th className={detHead(true)}>{t('Tipo %')}</th>}
                        {altB && <th className={detHead()}>{t('Cuota')}</th>}
                        {altB && <th className={detHead()}>{t('Interés')}</th>}
                        {altB && <th className={detHead()}>{t('Saldo')}</th>}
                        <th className={detHead(true)}>{t('vs A')}</th>
                        {altB && <th className={detHead()}>{t('vs B')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {filasCuadro.map(({ a, b, c, isYearEnd }, di) => {
                        const rowBg = rowBgFor(di, isYearEnd);
                        const sobreA = a.interes - b.interes;
                        const sobreB = c ? a.interes - c.interes : null;
                        const eurVal = verConviv ? getIndice('EURIBOR12', a.k, serie) : null;
                        const miborVal = verConviv ? getIndice('MIBOR', a.k, serie) : null;
                        return (
                          <tr
                            key={a.k}
                            className={
                              'group hover:bg-primary-50 dark:hover:bg-gray-700/70 ' +
                              rowBg +
                              (isYearEnd ? ' font-semibold' : '')
                            }
                          >
                            <td className={mesCell(rowBg)}>{a.k}</td>
                            <td className={dataCell(true)}>{fmt(a.tipo, 3)}</td>
                            <td className={dataCell()}>{eur(a.cuota)}</td>
                            <td className={dataCell()}>{eur(a.interes)}</td>
                            <td className={dataCell()}>{eur(a.amort + (a.extra || 0))}</td>
                            <td className={dataCell()}>{eur(a.saldo)}</td>
                            {verConviv && (
                              <td className={dataCell(true)}>
                                {eurVal === null || eurVal === undefined ? '–' : fmt(eurVal, 3)}
                              </td>
                            )}
                            {verConviv && (
                              <td className={dataCell()}>
                                {miborVal === null || miborVal === undefined ? '–' : fmt(miborVal, 3)}
                              </td>
                            )}
                            <td className={dataCell(true)}>{fmt(b.tipo, 3)}</td>
                            <td className={dataCell()}>{eur(b.cuota)}</td>
                            <td className={dataCell()}>{eur(b.interes)}</td>
                            <td className={dataCell()}>{eur(b.saldo)}</td>
                            {c && <td className={dataCell(true)}>{fmt(c.tipo, 3)}</td>}
                            {c && <td className={dataCell()}>{eur(c.cuota)}</td>}
                            {c && <td className={dataCell()}>{eur(c.interes)}</td>}
                            {c && <td className={dataCell()}>{eur(c.saldo)}</td>}
                            <td className={dataCell(true) + deltaCls(sobreA)}>{eur(sobreA)}</td>
                            {c && <td className={dataCell() + deltaCls(sobreB)}>{eur(sobreB)}</td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </ScrollTable>
                </div>
              )}

              {/* Desglose anual */}
              {resTab === 'anual' && (
                <div>
                  <ScrollTable>
                    <thead>
                      <tr>
                        <th rowSpan={2} className={cornerHead}>{t('Año')}</th>
                        <th colSpan={altB ? 3 : 2} className={grpHead('neutral', true)}>{t('Intereses')}</th>
                        <th colSpan={altB ? 3 : 2} className={grpHead('neutral', true)}>{t('Capital')}</th>
                        <th colSpan={altB ? 2 : 1} className={grpHead('sob', true)}>{t('Sobrecoste')}</th>
                        <th colSpan={altB ? 2 : 1} className={grpHead('acum', true)}>{t('Acumulado')}</th>
                      </tr>
                      <tr>
                        <th className={detHead(true)}>{t('IRPH')}</th>
                        <th className={detHead()}>{t('Alt. A')}</th>
                        {altB && <th className={detHead()}>{t('Alt. B')}</th>}
                        <th className={detHead(true)}>{t('IRPH')}</th>
                        <th className={detHead()}>{t('Alt. A')}</th>
                        {altB && <th className={detHead()}>{t('Alt. B')}</th>}
                        <th className={detHead(true)}>{t('vs A')}</th>
                        {altB && <th className={detHead()}>{t('vs B')}</th>}
                        <th className={detHead(true)}>{t('vs A')}</th>
                        {altB && <th className={detHead()}>{t('vs B')}</th>}
                      </tr>
                    </thead>
                    <tbody>
                      {derivados.anual.map((a, di) => {
                        const rowBg = rowBgFor(di, false);
                        return (
                          <tr
                            key={a.year}
                            className={'group hover:bg-primary-50 dark:hover:bg-gray-700/70 ' + rowBg}
                          >
                            <td className={mesCell(rowBg)}>{a.year}</td>
                            <td className={dataCell(true)}>{eur(a.intI)}</td>
                            <td className={dataCell()}>{eur(a.intA)}</td>
                            {altB && <td className={dataCell()}>{eur(a.intB)}</td>}
                            <td className={dataCell(true)}>{eur(a.capI)}</td>
                            <td className={dataCell()}>{eur(a.capA)}</td>
                            {altB && <td className={dataCell()}>{eur(a.capB)}</td>}
                            <td className={dataCell(true) + deltaCls(a.sA)}>{eur(a.sA)}</td>
                            {altB && <td className={dataCell() + deltaCls(a.sB)}>{eur(a.sB)}</td>}
                            <td className={dataCell(true)}>{eur(a.acumA)}</td>
                            {altB && <td className={dataCell()}>{eur(a.acumB)}</td>}
                          </tr>
                        );
                      })}
                    </tbody>
                  </ScrollTable>
                </div>
              )}

              {/* Convivencia 1999 (Euríbor vs MIBOR) */}
              {resTab === 'conv1999' && conv1999.rows.length > 0 && (
                <div>
                  <div className="mb-3 bg-primary-50/60 dark:bg-primary-900/20 border-l-4 border-primary-400 px-3 py-2 text-xs text-primary-800 dark:text-primary-300 rounded-r">
                    {t('1999 es el único año en que el Euríbor (publicado desde ene/1999) y el MIBOR coexistieron, y no son idénticos. Por eso el préstamo arroja DOS sobrecostes distintos según con qué índice se calcule ese año. Antes de 1999 ambos usan MIBOR y desde 2000 ambos usan Euríbor (el MIBOR no se tiene en cuenta a partir de 2000), así que la diferencia se origina solo en estos meses.')}
                  </div>
                  {/* Los dos resultados totales del préstamo según el índice de 1999 */}
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-2.5 mb-3">
                    <Kpi label={t('Sobrecoste A · 1999 con Euríbor')} value={eur(conv1999.sobreE)} note={t('Criterio actual del titular')} tone="bad" />
                    <Kpi label={t('Sobrecoste A · 1999 con MIBOR')} value={eur(conv1999.sobreM)} tone="bad" />
                    <Kpi label={t('Diferencia según el índice de 1999')} value={eur(conv1999.sobreM - conv1999.sobreE)} note={t('Impacto de elegir MIBOR en 1999')} />
                  </div>
                  <ScrollTable>
                    <thead>
                      <tr>
                        <th rowSpan={2} className={cornerHead}>{t('Mes')}</th>
                        <th colSpan={2} className={grpHead('a', true)}>{t('Con Euríbor')}</th>
                        <th colSpan={2} className={grpHead('b', true)}>{t('Con MIBOR')}</th>
                        <th rowSpan={2} className={grpHead('sob', true)}>{t('Δ Interés (Eur − MIBOR)')}</th>
                      </tr>
                      <tr>
                        <th className={detHead(true)}>{t('Tipo %')}</th>
                        <th className={detHead()}>{t('Interés')}</th>
                        <th className={detHead(true)}>{t('Tipo %')}</th>
                        <th className={detHead()}>{t('Interés')}</th>
                      </tr>
                    </thead>
                    <tbody>
                      {conv1999.rows.map((r, di) => {
                        const rowBg = rowBgFor(di, false);
                        const d = r.eInt - r.mInt;
                        return (
                          <tr
                            key={r.k}
                            className={'group hover:bg-primary-50 dark:hover:bg-gray-700/70 ' + rowBg}
                          >
                            <td className={mesCell(rowBg)}>{r.k}</td>
                            <td className={dataCell(true)}>{fmt(r.eTipo, 3)}</td>
                            <td className={dataCell()}>{eur(r.eInt)}</td>
                            <td className={dataCell(true)}>{fmt(r.mTipo, 3)}</td>
                            <td className={dataCell()}>{eur(r.mInt)}</td>
                            <td className={dataCell(true) + deltaCls(d)}>{eur(d)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </ScrollTable>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">
                    {t('Intereses solo de 1999')} —{' '}
                    {t('con Euríbor')}: <strong>{eur(conv1999.rows.reduce((s, r) => s + r.eInt, 0))}</strong> ·{' '}
                    {t('con MIBOR')}: <strong>{eur(conv1999.rows.reduce((s, r) => s + r.mInt, 0))}</strong> ·{' '}
                    {t('diferencia')}:{' '}
                    <strong>{eur(conv1999.rows.reduce((s, r) => s + r.eInt - r.mInt, 0))}</strong>.
                  </p>
                </div>
              )}

              {/* Serie de índices */}
              {resTab === 'serie' && (
                <div>
                  <p className="text-xs text-gray-500 dark:text-gray-400 mb-2">
                    {t('Serie histórica oficial de índices (Banco de España / BOE).')}
                  </p>
                  <div className="mb-2 bg-amber-50/70 dark:bg-amber-900/20 border-l-4 border-amber-400 px-3 py-2 text-xs text-amber-800 dark:text-amber-300 rounded-r">
                    {t('MIBOR: índice oficial de las hipotecas a tipo variable hasta la llegada del euro; solo se tiene en cuenta hasta dic/1999. Los valores de MIBOR posteriores a 2000 se muestran a título informativo, pero no intervienen en ningún cálculo. Euríbor 12m: publicado desde enero de 1999. Las filas resaltadas (1999) marcan el único año en que ambos coexisten como referencia.')}
                  </div>
                  <ScrollTable>
                    <thead>
                      <tr>
                        <th className={cornerHead}>{t('Mes')}</th>
                        <th className={soloHead(true)}>IRPH-CE %</th>
                        <th className={soloHead()}>IRPH Cajas %</th>
                        <th className={soloHead()}>IRPH Bancos %</th>
                        <th className={soloHead()}>Euríbor 12m %</th>
                        <th className={soloHead()}>MIBOR %</th>
                      </tr>
                    </thead>
                    <tbody>
                      {serieRows.map((k, di) => {
                        const r = serie[k];
                        // Convivencia real: 1999, único año con ambos índices
                        // publicados y ambos válidos para el cálculo.
                        const coex =
                          k.startsWith('1999') &&
                          r.eur !== null && r.eur !== undefined &&
                          r.mibor !== null && r.mibor !== undefined;
                        const rowBg = coex
                          ? 'bg-amber-50 dark:bg-amber-950'
                          : rowBgFor(di, false);
                        return (
                          <tr
                            key={k}
                            title={coex ? t('MIBOR y Euríbor coexistieron como referencia en este mes.') : undefined}
                            className={
                              'group hover:bg-primary-50 dark:hover:bg-gray-700/70 ' +
                              rowBg +
                              (coex ? ' font-semibold' : '')
                            }
                          >
                            <td className={mesCell(rowBg)}>{k}</td>
                            <td className={dataCell(true)}>{r.ce === null ? '–' : fmt(r.ce, 3)}</td>
                            <td className={dataCell()}>{r.cajas === null ? '–' : fmt(r.cajas, 3)}</td>
                            <td className={dataCell()}>{r.bancos === null ? '–' : fmt(r.bancos, 3)}</td>
                            <td className={dataCell()}>{r.eur === null ? '–' : fmt(r.eur, 3)}</td>
                            <td className={dataCell()}>{r.mibor === null ? '–' : fmt(r.mibor, 3)}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </ScrollTable>
                </div>
              )}

              {/* Jurisprudencia */}
              {resTab === 'jurisp' && (
                <div>
                  <h3 className="text-base font-semibold text-primary-700 dark:text-primary-300 mb-2">
                    {t('Marco jurisprudencial clave para la reclamación IRPH')}
                  </h3>
                  {JURISPRUDENCIA.map((bloque, i) => (
                    <details
                      key={i}
                      open={i === 0}
                      className="bg-amber-50/60 dark:bg-gray-700/40 border border-amber-200 dark:border-gray-600 rounded-lg px-4 py-3 mt-2.5"
                    >
                      <summary className="cursor-pointer font-semibold text-primary-700 dark:text-primary-300 text-sm">
                        {bloque.titulo}
                      </summary>
                      <ul className="mt-2 pl-5 list-disc space-y-1">
                        {bloque.items.map((it, j) => (
                          <li key={j} className="text-sm text-gray-600 dark:text-gray-300">
                            {it}
                          </li>
                        ))}
                      </ul>
                    </details>
                  ))}
                  <div className="mt-3 bg-red-50 dark:bg-red-900/20 border-l-4 border-red-500 px-3 py-2 text-xs text-red-700 dark:text-red-300 rounded-r">
                    {t('Nota legal: Esta calculadora aporta un cálculo financiero a efectos de cuantificación pericial. No constituye dictamen jurídico ni garantía de resultado. Datos de los índices: Banco de España y BOE.')}
                  </div>
                </div>
              )}
            </>
          )}
        </section>
      </div>
    </div>
  );
};

const LBL_INDICE = {
  IRPH_CE: 'IRPH Conjunto Entidades',
  IRPH_CAJAS: 'IRPH Cajas (hasta oct 2013)',
  IRPH_BANCOS: 'IRPH Bancos (hasta oct 2013)',
  MIBOR: 'MIBOR (anterior a 2000)',
  EURIBOR12: 'Euríbor 12 meses',
  EUR_MIBOR: 'Euríbor / MIBOR (mercado continuo)',
  NULO: 'Sin índice (sólo diferencial)',
};
const LBL_REDONDEO = {
  '0': 'Ninguno',
  '0.25': 'Al cuartillo (0,25)',
  '0.125': 'Al octavo (0,125)',
  '0.05': 'A 0,05',
  '0.01': 'A 0,01',
};
const LBL_SUPRESION = {
  DA15: 'Migración legal a IRPH-CE (DA 15ª L.14/2013)',
  congelar: 'Congelar último valor publicado (oct/2013)',
  euribor: 'Sustituir por Euríbor + diferencial original',
  fijo: 'Convertir a tipo fijo (último IRPH + diferencial)',
};

const JURISPRUDENCIA = [
  {
    titulo: 'STJUE (TJUE) — sentencias relevantes',
    items: [
      'STJUE 3 marzo 2020, C-125/18 Gómez del Moral/Bankia: el IRPH es susceptible de control de transparencia; la entidad debe haber informado al consumidor de la fórmula de cálculo y de su evolución pasada.',
      'STJUE 13 julio 2023, C-265/22 (Banco Santander): es pertinente para el control de transparencia que el consumidor conociera la Circular 5/1994 BdE, que advertía de la necesidad de aplicar un diferencial negativo al IRPH.',
      'STJUE 12 diciembre 2024, C-300/23: reitera la posibilidad de declarar abusivo el IRPH por falta de transparencia y aclara los criterios sobre las consecuencias económicas significativas.',
      'Asuntos C-561/21 y C-655/20: sobre tutela judicial efectiva y momento del inicio del cómputo de la prescripción para reclamaciones restitutorias.',
    ],
  },
  {
    titulo: 'Tribunal Supremo — evolución',
    items: [
      'STS 669/2017: excluía el IRPH del control de transparencia. Voto particular del Magistrado Orduña abogando por el control reforzado.',
      'STS 595-598/2020, 21/10/2020: aprecia falta de transparencia pero descarta la abusividad.',
      'STS 42-44/2022, 27/01/2022 (Kutxabank): mantiene la postura no abusivista.',
      'STS Pleno 1590/2025 y 1591/2025, 11/11/2025: consolida la postura del TS tras la STJUE C-300/23; motiva nuevas cuestiones prejudiciales (Tribunal Instancia Palma 11/03/2026).',
    ],
  },
  {
    titulo: 'Normativa esencial',
    items: [
      'Circular 8/1990 BdE — creación del IRPH (derogada).',
      'Circular 5/1994 BdE — advertencia: el IRPH incorpora TAE (comisiones) y exige diferencial negativo para igualar el mercado.',
      'Orden EHA/2899/2011 — tipos oficiales del mercado hipotecario en vigor.',
      'Circular 5/2012 BdE (Anejo 8) — definición vigente del IRPH-CE.',
      'Ley 14/2013 (DA 15ª) — supresión del IRPH Cajas/Bancos y CECA desde 1/11/2013; régimen de sustitución legal.',
      'Directiva 93/13/CEE y Directiva 2014/17/UE; LCGC 7/1998; TRLGDCU.',
    ],
  },
  {
    titulo: 'Estrategia probatoria para la pericial',
    items: [
      'Aportar la escritura y comprobar la redacción literal de la cláusula del índice y la cláusula de sustitución.',
      'Acreditar la no entrega de información precontractual sobre evolución histórica del IRPH ni del Anejo VIII de la Circular 5/1994.',
      'Realizar pericial financiera del sobrecoste real hasta la fecha de demanda y proyección hasta fin de préstamo.',
      'Plantear escenarios: Euríbor + 0% (más favorable), Euríbor + diferencial original (defensa banco), y media histórica del mercado.',
      'Valorar la prescripción: cuotas anteriores a cinco años desde la última actuación interruptiva.',
    ],
  },
];

export default CalculadoraIRPH;
